import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import payrollnootLogoLg from "assets/images/payrollnoot-logo-lg.png";
import axiosInstance from "../../axios/axiosInstance";
import "./login.scss";

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Invalid or missing reset token. Please request a new password reset.");
            return;
        }
        // Validate token on page load — only marks error if server confirms it's invalid
        axiosInstance.get(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`)
            .then(() => { /* token is valid — do nothing, let user fill the form */ })
            .catch((err) => {
                // Only show error if server explicitly says the token is bad (4xx)
                // Ignore network/config errors so a fresh link is never falsely rejected
                if (err.response && err.response.status >= 400 && err.response.status < 500) {
                    setError(err.response?.data?.message || "This reset link is invalid or has expired. Please request a new one.");
                }
            });
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await axiosInstance.post("/auth/reset-password", { token, newPassword });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "This reset link is invalid or has expired.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page" style={{ justifyContent: "center" }}>
            <div className="login-right" style={{ width: "100%", maxWidth: 460, boxShadow: "0 8px 40px rgba(0,0,0,0.10)" }}>
                <div className="login-right-inner">
                    <div className="login-brand">
                        <div className="login-brand-logo">
                            <img src={payrollnootLogoLg} alt="PayrollNoot" />
                        </div>
                    </div>

                    {success ? (
                        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
                            <div style={{ width: 56, height: 56, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h2 className="login-heading" style={{ marginTop: 0, marginBottom: 6 }}>Password Reset!</h2>
                            <p className="login-subheading" style={{ marginBottom: 28 }}>
                                Your password has been updated successfully. You can now sign in with your new password.
                            </p>
                            <button
                                className="login-submit-btn"
                                style={{ background: "#1B75F0" }}
                                onClick={() => navigate("/login")}
                            >
                                Back to Sign In
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="login-form-header">
                                <h2 className="login-heading" style={{ marginTop: 0, marginBottom: 4 }}>
                                    Set New Password
                                </h2>
                                <p className="login-subheading">
                                    Enter a new password for your Payroll Noot account.
                                </p>
                            </div>

                            {error && <div className="login-error">{error}</div>}

                            <form className="login-form" onSubmit={handleSubmit}>
                                <div className="login-field">
                                    <label className="login-label">New Password</label>
                                    <div className="login-input-wrap">
                                        <input
                                            className="login-input"
                                            type={showNew ? "text" : "password"}
                                            placeholder="At least 8 characters"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            autoFocus
                                            disabled={!token}
                                        />
                                        <button
                                            type="button"
                                            className="login-toggle-pw"
                                            onClick={() => setShowNew(v => !v)}
                                        >
                                            {showNew ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                </div>

                                <div className="login-field">
                                    <label className="login-label">Confirm Password</label>
                                    <div className="login-input-wrap">
                                        <input
                                            className="login-input"
                                            type={showConfirm ? "text" : "password"}
                                            placeholder="Repeat your new password"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            disabled={!token}
                                        />
                                        <button
                                            type="button"
                                            className="login-toggle-pw"
                                            onClick={() => setShowConfirm(v => !v)}
                                        >
                                            {showConfirm ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="login-submit-btn"
                                    style={{ background: "#1B75F0" }}
                                    disabled={loading || !token}
                                >
                                    {loading ? "Resetting…" : "Reset Password"}
                                </button>

                                <button
                                    type="button"
                                    className="login-back-btn"
                                    style={{ width: "100%", justifyContent: "center", marginTop: 4, marginBottom: 0 }}
                                    onClick={() => navigate("/login")}
                                >
                                    ← Back to Sign In
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
